import React from 'react'
import {
  Box,
  Typography,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { RelocateState } from '../types'
import { formatSkuLabel } from '../utils/dunUtils'

interface RelocateHeaderProps {
  sharedState: RelocateState
}

export const RelocateHeader: React.FC<RelocateHeaderProps> = ({
  sharedState
}) => {
  const { sku, productOriginName, isGreenSale, isDun, dunQty } = sharedState
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  if (!sku || !productOriginName) {
    return null
  }

  // En modo DUN el SKU se presenta junto a la unidad de medida y las unidades que
  // contiene la caja: es la referencia que el operador necesita a la vista mientras
  // ingresa cantidades, porque lo que tipea son cajas y no unidades.
  const skuLabel = `SKU: ${formatSkuLabel(sharedState)}`

  const dunHint = isDun ? (
    <Box
      display='flex'
      alignItems='center'
      justifyContent='center'
      mt={isMobile ? 1 : 0.5}
      px={1}
    >
      <InfoOutlinedIcon
        style={{
          fontSize: 16,
          color: theme.palette.text.secondary,
          marginRight: 6,
          flexShrink: 0
        }}
      />
      <Typography
        variant='caption'
        style={{ color: theme.palette.text.secondary, textAlign: 'center' }}
      >
        {`Las unidades ingresadas se calcularán por el contenido del total del DUN (X${dunQty}).`}
      </Typography>
    </Box>
  ) : null

  const greenSaleChip = isGreenSale ? (
    <Chip
      icon={<EventAvailableIcon style={{ color: 'white', fontSize: 18 }} />}
      label='Venta en Verde'
      size={isMobile ? 'small' : 'medium'}
      style={{
        backgroundColor: '#2e7d32',
        color: '#ffffff',
        fontWeight: 600,
        border: 'none',
        ...(isMobile
          ? { marginBottom: 8, height: 20, fontSize: '0.70rem' }
          : { marginRight: '8px', fontSize: '0.8125rem' })
      }}
    />
  ) : null

  if (isMobile) {
    // Layout compacto para mobile: chips apilados y centrados.
    return (
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        mb={1}
        p={1}
        bgcolor='#f5f5f5'
        borderRadius='4px'
      >
        <Typography
          variant='caption'
          style={{
            color: theme.palette.text.secondary,
            marginBottom: 4
          }}
        >
          Reubicando
        </Typography>

        {greenSaleChip}

        <Chip
          label={`Marca: ${productOriginName}`}
          color='primary'
          variant='outlined'
          size='small'
          style={{ fontWeight: 600, marginBottom: 4, maxWidth: '100%' }}
        />
        <Chip
          label={skuLabel}
          color='primary'
          variant='outlined'
          size='small'
          style={{ fontWeight: 600, maxWidth: '100%' }}
        />

        {dunHint}
      </Box>
    )
  }

  // Layout completo para desktop: misma información en una sola fila.
  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
      mb={1}
      p={1}
      bgcolor='#f5f5f5'
      borderRadius='8px'
    >
      <Box
        display='flex'
        flexDirection='row'
        alignItems='center'
        justifyContent='center'
        flexWrap='wrap'
      >
        <Typography variant='body1' style={{ marginRight: '12px' }}>
          Reubicando
        </Typography>

        {greenSaleChip}

        <Chip
          label={`Marca: ${productOriginName}`}
          color='primary'
          variant='outlined'
          style={{ marginRight: '8px', fontSize: '0.8125rem' }}
        />
        <Chip
          label={skuLabel}
          color='primary'
          variant='outlined'
          style={{ fontSize: '0.8125rem' }}
        />
      </Box>

      {dunHint}
    </Box>
  )
}
